// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import { FacebookCombinedAnalyticsPixelType, FacebookCombinedAnalyticsType } from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Switch, Grid, Typography } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'
import { Trash2, PlusSquare } from 'react-feather'
import InputTextField from 'src/components/common/form-elements/InputTextField'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig, verifyAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialCombinedFacebookConversionState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLocale } from 'src/lib/hooks/useLocal'
import produce from 'immer'
import { getDefaultChannels } from 'src/lib/utils/helpers'
import { parseFacebookAnalyticsPixelsPayload, parseFacebookConversionPixelsPaylod } from 'src/lib/utils/parsers'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'

interface FBPixelFormProps {
  combinedfacebookAnalyticsPixel: FacebookCombinedAnalyticsPixelType
  index: number
  combinedFacebookAnalytics: FacebookCombinedAnalyticsType
  setCombinedFacebookAnalytics: React.Dispatch<React.SetStateAction<FacebookCombinedAnalyticsType>>
}

const FBPixelForm: React.FC<FBPixelFormProps> = ({
  combinedfacebookAnalyticsPixel,
  index,
  combinedFacebookAnalytics,
  setCombinedFacebookAnalytics
}) => {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)

  const removeFBPixel = async () => {
    setCombinedFacebookAnalytics(
      produce((draft: any) => {
        draft?.pixels?.splice(index, 1)
      })
    )
  }

  return (
    <>
      <IntegrationChannels
        channels={combinedfacebookAnalyticsPixel.channels ?? []}
        onChange={(channels: any) => {
          const newAnalyticsPixel: FacebookCombinedAnalyticsPixelType = { ...combinedfacebookAnalyticsPixel }
          newAnalyticsPixel.channels = channels
          const modifiedAnalyticsPixels = [...combinedFacebookAnalytics?.pixels]
          modifiedAnalyticsPixels[index] = newAnalyticsPixel
          setCombinedFacebookAnalytics({ ...combinedFacebookAnalytics, pixels: modifiedAnalyticsPixels })
        }}
        hideChannels={!combinedFacebookAnalytics?.enabled}
      >
        <Grid display={'flex'} flexDirection={'column'} sx={{ gap: 2, width: '100%' }}>
          <div className='flex justify-start gap-2'>
            <div className='flex items-center justify-between w-full'>
              <Typography variant='body1' className='min-w-fit' sx={{ fontWeight: 600 }}>
                Pixel {index + 1}
              </Typography>
              <button
                className='flex items-center text-sm font-semibold bg-white text-coal-dark w-fit'
                onClick={() => setIsDeleteConfirmOpen(!isDeleteConfirmOpen)}
              >
                <Trash2 size='16' />
              </button>
            </div>
          </div>

          <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
            <Grid display={'flex'} alignItems={'center'} gap={19}>
              <FloCheckbox
                value={combinedfacebookAnalyticsPixel?.enabled}
                label='Enable this pixel'
                setValue={(checked: boolean) => {
                  const newAnalyticsPixel = { ...combinedfacebookAnalyticsPixel }
                  newAnalyticsPixel.enabled = checked
                  const modifiedAnalyticsPixels = [...combinedFacebookAnalytics?.pixels]
                  modifiedAnalyticsPixels[index] = newAnalyticsPixel
                  setCombinedFacebookAnalytics({
                    ...combinedFacebookAnalytics,
                    pixels: modifiedAnalyticsPixels
                  })
                }}
              />
            </Grid>
          </Grid>
          {Boolean(combinedfacebookAnalyticsPixel?.enabled) && (
            <>
              <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                <Grid display={'flex'} alignItems={'center'} gap={19}>
                  <InputTextField
                    textFieldId={`facebook-pixel-id-${index + 1}`}
                    label='Pixel ID'
                    inputPlaceholder='Facebook Pixel ID'
                    inputProps={{ 'aria-label': 'controlled' }}
                    inputValue={combinedfacebookAnalyticsPixel?.pixelId}
                    handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const value = event?.target?.value
                      const newAnalyticsPixel = { ...combinedfacebookAnalyticsPixel }
                      newAnalyticsPixel.pixelId = value
                      const modifiedAnalyticsPixels = [...combinedFacebookAnalytics?.pixels]
                      modifiedAnalyticsPixels[index] = newAnalyticsPixel
                      setCombinedFacebookAnalytics({
                        ...combinedFacebookAnalytics,
                        pixels: modifiedAnalyticsPixels
                      })
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                <Grid display={'flex'} alignItems={'center'} gap={19}>
                  <FloCheckbox
                    value={combinedfacebookAnalyticsPixel?.conversionsEnabled}
                    label='Enable Conversions API for this pixel'
                    setValue={(checked: boolean) => {
                      const newConversionPixel = { ...combinedfacebookAnalyticsPixel }
                      newConversionPixel.conversionsEnabled = checked
                      const modifiedConversionPixels = [...combinedFacebookAnalytics?.pixels]
                      modifiedConversionPixels[index] = newConversionPixel
                      setCombinedFacebookAnalytics({
                        ...combinedFacebookAnalytics,
                        pixels: modifiedConversionPixels
                      })
                    }}
                  />
                </Grid>
              </Grid>
              {Boolean(combinedfacebookAnalyticsPixel?.conversionsEnabled) && (
                <>
                  <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                    <Grid display={'flex'} alignItems={'center'} gap={19}>
                      <InputTextField
                        textFieldId={`facebook-pixel-api-token${index + 1}`}
                        label='API Token'
                        inputPlaceholder='Facebook Conversion API Token'
                        inputProps={{ 'aria-label': 'controlled' }}
                        inputValue={combinedfacebookAnalyticsPixel?.apiToken}
                        handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                          const value = event?.target?.value
                          const newConversionPixel = { ...combinedfacebookAnalyticsPixel }
                          newConversionPixel.apiToken = value
                          const modifiedConversionPixels = [...combinedFacebookAnalytics?.pixels]
                          modifiedConversionPixels[index] = newConversionPixel
                          setCombinedFacebookAnalytics({
                            ...combinedFacebookAnalytics,
                            pixels: modifiedConversionPixels
                          })
                        }}
                        isMultiline={true}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </IntegrationChannels>
      {isDeleteConfirmOpen ? (
        <ConfirmationDialog
          jobButton={'Yes, remove this pixel'}
          cancelButton={'No'}
          jobButtonColor='error'
          cancelButtonColor='secondary'
          confirmationText={'Are you sure you want to remove this pixel?'}
          title={'Remove pixel'}
          isOpen={isDeleteConfirmOpen}
          setIsConfirmationOpen={setIsDeleteConfirmOpen}
          handleJob={removeFBPixel}
        />
      ) : (
        <></>
      )}
    </>
  )
}

interface FacebookAnalyticsProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const FacebookAnalytics: React.FC<FacebookAnalyticsProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()
  const { facebook } = integrations || {}

  //Local State
  const [combinedFacebookAnalytics, setCombinedfacebookAnalytics] = useState<FacebookCombinedAnalyticsType>(
    initialCombinedFacebookConversionState
  )
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('rify')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setCombinedfacebookAnalytics(temporary.integrations?.facebook)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  const onAddFBPixelBtnClick = async () => {
    const defaultChannels = await getDefaultChannels()
    const modifiedCombinedfacebookPixels = [...combinedFacebookAnalytics?.pixels]
    modifiedCombinedfacebookPixels.push({
      enabled: true,
      pixelId: '',
      conversionsEnabled: false,
      apiToken: '',
      channels: defaultChannels
    })
    setCombinedfacebookAnalytics({
      ...combinedFacebookAnalytics,
      pixels: modifiedCombinedfacebookPixels
    })
  }

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(facebook) !== JSON.stringify(combinedFacebookAnalytics)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facebook, combinedFacebookAnalytics])

  useEffect(() => {
    if (!Boolean(facebook) || !Boolean(facebook?.pixels?.length)) return
    setCombinedfacebookAnalytics(facebook)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [facebook, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            facebook: combinedFacebookAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const getPayload = (type: string) => {
    switch (type) {
      case 'facebook':
        return {
          enabled: combinedFacebookAnalytics?.enabled,
          pixel_id: parseFacebookAnalyticsPixelsPayload(combinedFacebookAnalytics?.pixels)
        }
      case 'fbConversion':
        return {
          enabled: combinedFacebookAnalytics?.enabled,
          pixels: parseFacebookConversionPixelsPaylod(combinedFacebookAnalytics?.pixels)
        }
      default:
        return {
          enabled: combinedFacebookAnalytics?.enabled,
          pixel_id: parseFacebookAnalyticsPixelsPayload(combinedFacebookAnalytics?.pixels)
        }
    }
  }

  const validateFields = () => {
    try {
      const facebookPixels: string[] = []
      combinedFacebookAnalytics?.pixels?.forEach((pixel: FacebookCombinedAnalyticsPixelType, index: number) => {
        facebookPixels.push(pixel?.pixelId)
        if (combinedFacebookAnalytics?.enabled) {
          if (Boolean(pixel?.enabled) && !Boolean(pixel?.pixelId?.length)) {
            setOpenAlert('error')
            setAlertMessage(`Please enter a valid facebook pixel id in Pixel ${index + 1}!`)
            throw new Error(`Please enter a valid facebook pixel id in Pixel ${index + 1}!`)
          }
          if (Boolean(pixel?.conversionsEnabled) && !Boolean(pixel?.apiToken?.length)) {
            setOpenAlert('error')
            setAlertMessage(`Please enter a valid facebook conversion api token in in Pixel ${index + 1}!`)
            throw new Error(`Please enter a valid facebook conversion api token in in Pixel ${index + 1}!`)
          }
        }
      })
      if (
        Boolean(
          facebookPixels?.filter((pixelId: string, index: number) => facebookPixels.indexOf(pixelId) !== index).length
        )
      ) {
        setOpenAlert('error')
        setAlertMessage('Duplacate pixel ids are not allowed!')
        throw new Error('Duplacate pixel ids are not allowed!')
      }
    } catch (e) {
      return false
    }
    return true
  }

  const onAnalyticsSubmit = async () => {
    setLoaderProperties({ isLoaderVisible: true })
    if (validateFields()) {
      try {
        const verifyResponse: any = await verifyAnalyticsConfig('fb_conversion', getPayload('fbConversion'))
        if (verifyResponse?.error) {
          setOpenAlert('error')
          setAlertMessage(verifyResponse?.error)
          console.error('x-trace-id:', verifyResponse.headers?.['x-trace-id'])
          setLoaderProperties({ isLoaderVisible: false })
          return
        }
        const [facebookAnalyticsResponse, facebookConversionResponse] = await await Promise.allSettled([
          setAnalyticsConfig('facebook', getPayload('facebook')),
          setAnalyticsConfig('fb_conversion', getPayload('fbConversion'))
        ])
        if (facebookAnalyticsResponse.status === 'rejected') {
          handleAlerts(
            alertSeverites.SEVERITY_ERROR,
            facebookAnalyticsResponse?.reason?.error ?? facebookAnalyticsResponse?.reason?.message
          )
          handleDiscardSaving()
          //callGetAnalytics()
        }
        if (facebookConversionResponse.status === 'rejected') {
          handleAlerts(
            alertSeverites.SEVERITY_ERROR,
            facebookConversionResponse?.reason?.error ?? facebookConversionResponse?.reason?.message
          )
          handleDiscardSaving()
          //callGetAnalytics()
        } else {
          setIntegrations({ ...integrations, facebook: combinedFacebookAnalytics }, false)
          setOpenAlert('success')
          setAlertMessage('Successfully saved analytics configuration!')
        }
        handleOnboardingSave && handleOnboardingSave()
      } catch (error: any) {
        const errorMessage = error?.response?.data?.errorMessage
          ? error?.response?.data?.errorMessage
          : `Could not save configuration!`
        handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
        console.error(error)
      }
    }
    setLoaderProperties({ isLoaderVisible: false })
    return
  }

  const handleDiscardSaving = () => {
    if (!facebook) return
    setCombinedfacebookAnalytics(facebook)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <LayoutContainer>
        <Grid display={'flex'} flexDirection={'column'} sx={{ gap: 2, width: '100%' }}>
          <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Facebook pixel is</Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${combinedFacebookAnalytics?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='facebookToggle'
                checked={combinedFacebookAnalytics?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const checked = event?.target?.checked
                  setCombinedfacebookAnalytics({ ...combinedFacebookAnalytics, enabled: checked })
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </LayoutContainer>

      {Boolean(combinedFacebookAnalytics?.enabled) && (
        <>
          {combinedFacebookAnalytics.pixels?.map((pixel: FacebookCombinedAnalyticsPixelType, index: number) => (
            <FBPixelForm
              key={index}
              combinedfacebookAnalyticsPixel={pixel}
              index={index}
              combinedFacebookAnalytics={combinedFacebookAnalytics}
              setCombinedFacebookAnalytics={setCombinedfacebookAnalytics}
            />
          ))}

          <div className='flex flex-wrap justify-end gap-4'>
            <button
              className='flex items-center gap-1 px-3 py-2 text-sm font-semibold bg-white border rounded-lg border-flo-gray-light text-coal-dark w-fit'
              onClick={onAddFBPixelBtnClick}
            >
              <PlusSquare size='16' />
              Add another pixel
            </button>
          </div>
        </>
      )}

      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={onAnalyticsSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={onAnalyticsSubmit}
        >
          {t('save')}
        </button>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default FacebookAnalytics
